import { useNavigate } from "react-router-dom";
import type { TestRun } from "../hooks/useTestRuns";

interface StatusCfg { bg: string; color: string; dot: string }

const DEFAULT_CFG: StatusCfg = { bg: "#374151", color: "#d1d5db", dot: "#6b7280" };

const STATUS_CFG: Record<string, StatusCfg> = {
  pending:   DEFAULT_CFG,
  running:   { bg: "#1e3a5f", color: "#93c5fd", dot: "#3b82f6" },
  passed:    { bg: "#14532d", color: "#86efac", dot: "#22c55e" },
  failed:    { bg: "#7f1d1d", color: "#fca5a5", dot: "#ef4444" },
  error:     { bg: "#78350f", color: "#fcd34d", dot: "#f59e0b" },
  cancelled: { bg: "#1f2937", color: "#9ca3af", dot: "#6b7280" },
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function durationMs(run: TestRun): string {
  if (!run.started_at || !run.finished_at) return "—";
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m${Math.floor((ms % 60000) / 1000)}s`;
}

interface Props {
  run: TestRun;
  selected: boolean;
  onClick: () => void;
  replayUrl?: string;
}

export default function TestRunCard({ run, selected, onClick, replayUrl }: Props) {
  const navigate = useNavigate();
  const cfg = STATUS_CFG[run.status] ?? DEFAULT_CFG;

  return (
    <div
      onClick={onClick}
      style={{
        background: selected ? "#1e293b" : "#111827",
        border: `1px solid ${selected ? "#3b82f6" : "#1f2937"}`,
        borderLeft: `4px solid ${cfg.dot}`,
        borderRadius: 8,
        padding: "10px 14px",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <span
          style={{
            background: cfg.bg,
            color: cfg.color,
            borderRadius: 4,
            padding: "1px 7px",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.04em",
            fontFamily: "monospace",
            flexShrink: 0,
          }}
        >
          {run.status.toUpperCase()}
        </span>
        <span
          style={{
            flex: 1,
            fontWeight: 600,
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {run.manifest_name}
        </span>
        <span style={{ fontSize: 11, color: "#6b7280", flexShrink: 0 }}>
          {durationMs(run)}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {run.app} &nbsp;·&nbsp; {relativeTime(run.created_at)}
        </span>
        {replayUrl && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(replayUrl);
            }}
            style={{
              background: "transparent",
              color: "#a855f7",
              border: "none",
              fontSize: 11,
              cursor: "pointer",
              padding: 0,
              fontWeight: 600,
            }}
          >
            ▶ Replay
          </button>
        )}
      </div>
    </div>
  );
}
